import React, { createContext, useContext, useState, useCallback } from 'react';
import InsufficientCreditsModal from '../components/InsufficientCreditsModal';

const CreditGuardContext = createContext(null);

export const useCreditGuard = () => {
  const ctx = useContext(CreditGuardContext);
  if (!ctx) return { showCreditModal: () => {} };
  return ctx;
};

export const CreditGuardProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    open: false,
    operation: '',
    cost: 0,
    balance: 0,
    message: '',
  });

  const showCreditModal = useCallback(({ operation, cost, balance, message }) => {
    setModalState({
      open: true,
      operation: operation || '',
      cost: cost || 0,
      balance: balance || 0,
      message: message || '',
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, open: false }));
  }, []);

  // Global interceptor: detect 403 insufficient credit errors from fetch responses
  React.useEffect(() => {
    const originalFetch = window.fetch;
    // Store original fetch globally so the modal can bypass the interceptor
    window.__originalFetch = originalFetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Only intercept 403 responses
      if (response.status === 403) {
        try {
          const cloned = response.clone();
          const body = await cloned.json();
          const detail = typeof body?.detail === 'string' ? body.detail : '';

          if (
            detail.toLowerCase().includes('insufficient credits') ||
            detail.toLowerCase().includes('out of credit')
          ) {
            // Parse cost and balance from the message
            const costMatch = detail.match(/costs?\s+(\d+)\s+credits/i);
            const balanceMatch = detail.match(/have\s+(\d+)/i);
            showCreditModal({
              operation: '',
              cost: costMatch ? parseInt(costMatch[1], 10) : 0,
              balance: balanceMatch ? parseInt(balanceMatch[1], 10) : 0,
              message: detail,
            });
          }
        } catch {
          // Ignore parse errors — not every 403 is a credit error
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
      delete window.__originalFetch;
    };
  }, [showCreditModal]);

  return (
    <CreditGuardContext.Provider value={{ showCreditModal }}>
      {children}
      <InsufficientCreditsModal
        open={modalState.open}
        onClose={closeModal}
        operation={modalState.operation}
        cost={modalState.cost}
        balance={modalState.balance}
        message={modalState.message}
      />
    </CreditGuardContext.Provider>
  );
};
